import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Currency from "src/react/components/currency";
import { INTERNATIONAL_POSTAGE_IN_PENCE } from "src/data/app-constants";
import { color } from "src/styles/variables";

function OrderSummary({
  discount,
  couponAppliedMessage,
  couponMinimumSpendTooLow,
  total = 0,
  orderValue = 0,
  hasInternationalPostage,
  giftWrappingCost,
  deliveryCost,
  children,
}) {
  const shouldShowDeliveryPostageWarning =
    discount === 0 && (hasInternationalPostage || !!deliveryCost);
  return (
    <Summary>
      <FlexItem data-selector="OrderSummary-subtotal">
        <span>Products</span>
        <Currency value={orderValue} />
      </FlexItem>
      {couponMinimumSpendTooLow && (
        <FlexItem>
          <Discount minimumSpendTooLow>{couponAppliedMessage}</Discount>
        </FlexItem>
      )}
      {discount > 0 && (
        <Fragment>
          <FlexItem data-selector="OrderSummary-coupon" bold>
            <span>Discount</span>
            <span>
              -<Currency value={discount} />
            </span>
          </FlexItem>
          <Subtotal warning={hasInternationalPostage || !!deliveryCost}>
            <FlexItem data-selector="OrderSummary-coupon">
              <span>
                Subtotal{(hasInternationalPostage || !!deliveryCost) && "*"}
              </span>
              <span>
                <Currency
                  value={discount < orderValue ? orderValue - discount : 0}
                />
              </span>
            </FlexItem>
          </Subtotal>
        </Fragment>
      )}
      {giftWrappingCost > 0 && (
        <FlexItem data-selector="OrderSummary-giftWrapping">
          <span>Gift wrapping</span>
          <span>
            <Currency value={giftWrappingCost} />
          </span>
        </FlexItem>
      )}
      <Delivery warning={shouldShowDeliveryPostageWarning}>
        {hasInternationalPostage ? (
          <FlexItem data-selector="OrderSummary-international-delivery">
            <span>
              International delivery{shouldShowDeliveryPostageWarning && "*"}
            </span>
            <Currency value={INTERNATIONAL_POSTAGE_IN_PENCE} />
          </FlexItem>
        ) : (
          <FlexItem data-selector="OrderSummary-delivery">
            <span>Delivery{shouldShowDeliveryPostageWarning && "*"}</span>
            {deliveryCost ? (
              <Currency value={deliveryCost} />
            ) : (
              <span>Free</span>
            )}
          </FlexItem>
        )}
      </Delivery>
      <Total data-selector="OrderSummary-total">
        <span>Order total</span>
        <Currency value={total} />
      </Total>
      {children}
    </Summary>
  );
}

OrderSummary.propTypes = {
  children: PropTypes.any,
  total: PropTypes.number,
  orderValue: PropTypes.number,
  discount: PropTypes.number,
  giftWrappingCost: PropTypes.number,
  deliveryCost: PropTypes.number,
  couponAppliedMessage: PropTypes.string,
  couponMinimumSpendTooLow: PropTypes.bool,
  hasInternationalPostage: PropTypes.bool,
};

export default OrderSummary;

const Summary = styled.section`
  font-size: 14px;
  font-weight: 400;
  display: flex;
  flex-direction: column;
`;

const FlexItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  ${({ bold }) => bold && "font-weight: 600;"}
`;

const Total = styled(FlexItem)`
  font-size: 20px;
  font-weight: 600;
  border-top: 1px solid ${color.dustyGrey};
  padding-top: 10px;
  margin: 5px 0 10px;
`;

const Discount = styled.p`
  text-align: right;
  margin-bottom: 0;
  ${({ minimumSpendTooLow }) =>
    minimumSpendTooLow &&
    `
    color: ${color.torchRed};
    text-align: left;
  `};
`;

const Delivery = styled.div`
  ${({ warning }) => warning && `color: ${color.darkOrange}`};
`;

const Subtotal = styled.div`
  ${({ warning }) => warning && `color: ${color.darkOrange}`};
  border-top: 1px solid ${color.dustyGrey};
  padding-top: 5px;
  margin: 5px 0 15px;
  font-size: 18px;
`;
